/**
 * ledgerPeriodAt
 *
 * The bounds of fiscal period `index`, counted from the structure's anchor in its zone. Both bounds
 * are computed from the anchor, never by chaining one period onto the last, so month-end clamping
 * stays consistent: an anchor on the 31st gives contiguous periods whose `endAt` is always the next
 * `startAt`. The anchor is read by `ledgerMomentOfValue`, so it must carry a date. Throws on an
 * invalid anchor, since every period decision would be wrong without it.
 * Consumers: `ledgerPeriodOf`, and period close and aging (SYD-2709, SYD-2711).
 */

import type { DurationLikeObject } from 'luxon';
import type { LedgerCalendarStructure, LedgerFiscalPeriodUnit } from '../System.Ledger.Definition.types';
import type { LedgerPeriod } from './LedgerPeriod.types';
import { ledgerMomentOfValue } from './ledgerMomentOfValue';

function periodsDuration(unit: LedgerFiscalPeriodUnit, count: number): DurationLikeObject {
    if (unit === 'week') return { weeks: count };
    if (unit === 'month') return { months: count };
    return { months: 3 * count };
}

function ledgerPeriodAt(index: number, structure: LedgerCalendarStructure): LedgerPeriod {
    const { unit, anchor: anchorText } = structure.fiscalPeriod;
    const anchor = ledgerMomentOfValue(anchorText, structure.timeZone);
    if (anchor === null) {
        throw new Error(`ledgerPeriodAt: anchor "${anchorText}" in zone "${structure.timeZone}" is not a valid moment`);
    }
    return {
        index,
        startAt: anchor.plus(periodsDuration(unit, index)),
        endAt: anchor.plus(periodsDuration(unit, index + 1)),
    };
}

export { ledgerPeriodAt };
